﻿import React, { useState, useEffect } from 'react';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';
import { SampleType } from '../../models/SamplePreparations/sample-type';
import { CrudFunctions } from '../../utilities/crud-functions';
import DataTemplates from '../../templates/data-templates';
import { Technology } from '../../models/technology';
import SampleTypesEditor from './SampleTypesEditor';
import DmaFooter from '../DmaFooter';

import DataGrid, {
    Column,
    FilterRow,
    Selection,
    Button as ColumnButton,
    HeaderFilter,
    Toolbar,
    Editing,
    Item as ToolbarItem,
    Paging,
    SearchPanel,
    LoadPanel
} from 'devextreme-react/data-grid';

import Button from 'devextreme-react/button';
import List from 'devextreme-react/list';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import { sample } from 'lodash';

let key = 0;

export default function SampleTypesForm() {
    const [technologies, setTechnologies] =
        useState<Technology[]>();

    const [selectedTechnologies, setSelectedTechnologies] =
        useState<Technology[]>();

    const [sampleTypes, setSampleTypes] =
        useState<SampleType[]>();

    const [selectedSampleType, setSelectedSampleType] =
        useState<SampleType>();

    const [newSampleType, setNewSampleType] =
        useState<boolean>();

    const [sampleTypesEditorDialogVisible, setSampleTypesEditorDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();  

    useEffect(() => {
        getAllData();
    }, []);

    key++;

    return (
        <div className="pw-content">
            <div className="lq-title-bar pw-form-header">
                <div className="lq-main-title">Sample Types</div>

                <div className="title-bar-spacer" />
                <div className="save-toolbar">
                    <Button id="goBackButton" text="" icon="arrowleft" type="normal"
                        hint="go back"
                        height={'2.6em'}
                        onClick={goBack} />
                </div>
            </div>

            <div className="pw-60-40-column-grid pw-margin-top-05">
                <div>
                    <DataGrid
                        height="30rem" width="100%"
                        dataSource={sampleTypes ? sampleTypes : []}
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode='widget'
                        keyExpr="sampleTypeId"
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        onSelectionChanged={sampleTypesSelectionChanged}
                        showBorders={true}>

                        <LoadPanel enabled={true} />
                        <Paging enabled={false} />
                        <Selection mode='single' />
                        <HeaderFilter visible={true} />
                        <FilterRow visible={true} />
                        <SearchPanel visible={true} />

                        <Column type="buttons" width="4rem">
                            <ColumnButton hint="edit" icon="edit"
                                onClick={onEditSampleTypeButtonClicked} />
                        </Column>
                        
                        <Column dataField='name' dataType='string' width='7rem' />
                        <Column dataField='sequence' dataType='number' width='7rem' />
                        <Column dataField='description' dataType='string' />

                        <Toolbar>
                            <ToolbarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">Sample Types</div>
                                </div>
                            </ToolbarItem>
                            <ToolbarItem location='after' name="searchPanel" />
                            <ToolbarItem location='after'>
                                <Button type="normal" icon="plus" hint="create a new sample type"
                                    onClick={onAddNewSampleTypeClick} />
                            </ToolbarItem>
                        </Toolbar>

                        <Editing mode="popup"
                            useIcons={true}
                            allowAdding={true}
                            allowUpdating={false}
                            allowDeleting={false} />
                    </DataGrid>
                </div>

                <div className="pw-margin-left-1" style={{marginTop: '3rem'}}>
                    <div className="pw-form-unit-label">Associated Technologies</div>

                    <List id="assetTechnologiesList"
                        height="24rem"
                        className="pw-bg-white"
                        items={selectedTechnologies ? selectedTechnologies : []}
                        keyExpr="technologyId"
                        showScrollbar="always"
                        itemRender={DataTemplates.EhsTemplate} />
                </div>
            </div>

            <DmaFooter />

            {sampleTypesEditorDialogVisible &&
                <DialogPopup visible={sampleTypesEditorDialogVisible}
                    onHiding={hideSampleTypesEditorDialog}
                    title="Edit Sample Type"
                    fullScreen={true} >
                    <SampleTypesEditor sampleTypeId={selectedSampleType && selectedSampleType.sampleTypeId}
                        technologies={technologies} isNew={newSampleType} onClose={sampleTypesEditorDialogClose} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata}
                onClose={onGlobalDialogClose} />
            }
        </div>
    );

    function onEditSampleTypeButtonClicked(e: any) {
        setSelectedSampleType(e.row.data);
        showSampleTypesEditorDialog();
    }

    function sampleTypesSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            let currentSampleType = e.selectedRowsData[0] as SampleType; 

            if (currentSampleType) {
                setSelectedSampleType(sample);

                let selectedSampleTypeTechnologies = currentSampleType.technologies;
                
                let sampleTypeTechnologList: Technology[] = [];

                selectedSampleTypeTechnologies.forEach(stt => {
                    const matchingTechnology = technologies.find(t => t.technologyId === stt);

                    if (matchingTechnology)
                        sampleTypeTechnologList.push(matchingTechnology);
                });             
                
                setSelectedTechnologies(sampleTypeTechnologList);
            }
        }
    }

    function onAddNewSampleTypeClick() {
        setNewSampleType(true);
        showSampleTypesEditorDialog();
    }

    function sampleTypesEditorDialogClose() {
        setNewSampleType(false);
        hideSampleTypesEditorDialog();
        getSampleNames();
    }

    async function getTechnologies() {
        await CrudFunctions.getData("common/benchsheettechnologytypes")
            .then(data => {
                setTechnologies(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Technology List");
            });
    }

    async function getSampleNames() {
        await CrudFunctions.getData("common/sampletypedata")
            .then(data => {
                   setSampleTypes(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Sample Types");
            });
    }

    async function getAllData() {
        await Promise.all([
            getSampleNames(),
            getTechnologies()
        ])
    }

    function goBack() {
        history.back();
    }

    function showSampleTypesEditorDialog() {
        setSampleTypesEditorDialogVisible(true);
    }

    function hideSampleTypesEditorDialog() {
        setSampleTypesEditorDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function onGlobalDialogClose() {
        clearGlobalDialog();
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}